import { queryOptions } from '@tanstack/react-query'
import type { ApiClient } from '@volee/api-client'
import type { components } from '@volee/api-types'

import { api } from '../services/api.ts'

// The calls the sign-up makes: the first three public, the last two behind the token alone —
// the `app_user` does not exist until an operator approves.

export type PublicClubRead = components['schemas']['PublicClubRead']
export type PublicClubList = components['schemas']['PublicClubList']
export type DocumentUploaded = components['schemas']['DocumentUploaded']
export type RegistrationCreate = components['schemas']['RegistrationCreate']
export type RegistrationSubmitted = components['schemas']['RegistrationSubmitted']
export type RegistrationStatusRead = components['schemas']['RegistrationStatusRead']

/* The four states the screens branch on. Declared here and not derived from
 * `RegistrationStatusRead['status']`, which the generator widens to `string`: a `Record` over
 * a widened type would accept any key and stop being the exhaustiveness check it is for. */
export const REGISTRATION_STATUSES = [
  'submitted',
  'needs_integration',
  'rejected',
  'approved',
] as const

export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]

/** The status the row carries, or `null` for a value this build does not know — a fifth
 *  state added server-side must not render as a blank screen. */
export function registrationStatusOf(status: string): RegistrationStatus | null {
  return (REGISTRATION_STATUSES as readonly string[]).includes(status)
    ? (status as RegistrationStatus)
    : null
}

/** `authenticated: false` on purpose: another account's still-valid Bearer on this call
 *  would make its 401 fire `onSessionExpired` in the middle of a sign-up. `onlyActive` is the
 *  player picker's; the official's keeps every status (issue #95). */
export function searchClubs(
  client: ApiClient,
  q: string,
  onlyActive = false,
): Promise<PublicClubList> {
  const query = onlyActive ? { q, only_active: true } : { q }
  return client.get<PublicClubList>('/v1/public/clubs', { query, authenticated: false })
}

/** The one write of step 4: the file goes up before the request it will belong to exists, so
 *  a refused upload is refused where the person still has the file picker in front of them. */
export function uploadDocument(client: ApiClient, file: File): Promise<DocumentUploaded> {
  const body = new FormData()
  body.append('file', file)
  // `post` takes the FormData as it is and leaves Content-Type unset: only fetch knows the
  // multipart boundary. Verified in packages/api-client/src/client.ts:114.
  return client.post<DocumentUploaded>('/v1/public/registration-documents', body, {
    authenticated: false,
  })
}

export function submitRegistration(
  client: ApiClient,
  body: RegistrationCreate,
): Promise<RegistrationSubmitted> {
  return client.post<RegistrationSubmitted>('/v1/public/registration-requests', body, {
    authenticated: false,
  })
}

/** Authenticated, and deliberately not through `current_user`: the caller holds a token and
 *  has no `app_user` row, which is the normal case here rather than a failure. */
export function readOwnRequest(client: ApiClient): Promise<RegistrationStatusRead> {
  return client.get<RegistrationStatusRead>('/v1/registration-requests/me')
}

/** The way back from `needs_integration`: uploading again returns the request to the queue. */
export function replaceOwnDocument(client: ApiClient, file: File): Promise<RegistrationStatusRead> {
  const body = new FormData()
  body.append('file', file)
  return client.post<RegistrationStatusRead>('/v1/registration-requests/me/document', body)
}

// Indirection with a purpose: a test replaces these, whereas a spy on this module's own
// exports cannot rebind a call the module makes to itself.
export const registrationReader = {
  searchClubs: (q: string, onlyActive = false) => searchClubs(api, q, onlyActive),
  readOwn: () => readOwnRequest(api),
}

export const registrationWriter = {
  uploadDocument: (file: File) => uploadDocument(api, file),
  submit: (body: RegistrationCreate) => submitRegistration(api, body),
  replaceDocument: (file: File) => replaceOwnDocument(api, file),
}

export const REGISTRATION_STATUS_KEY = ['registration-request', 'me'] as const

/** `retry: false` as in `meQueryOptions`: a caller with no request gets a 404, and that is
 *  THE answer — retrying only delays the screen that acts on it. */
export const registrationStatusQueryOptions = queryOptions({
  queryKey: REGISTRATION_STATUS_KEY,
  queryFn: () => registrationReader.readOwn(),
  retry: false,
})
